"use client";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin } from "lucide-react";

export default function ContactPage() {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Contact form submitted");
  };

  return (
    <div className="container mx-auto px-4 py-24 max-w-6xl">
      {/* Header section utilizing a safe glassmorphism background */}
      <div className="bg-slate-900/40 backdrop-blur-md border border-white/10 rounded-xl p-8 mb-12 text-center md:text-left shadow-lg">
        <h1 className="text-white mb-4 text-4xl font-bold tracking-tight">
          Contact Us
        </h1>
        <p className="text-slate-200">
          Get in touch with our team for enterprise solutions or any inquiries.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-start">
        {/* Contact Form Section */}
        <Card className="border border-white/10 bg-slate-900/40 backdrop-blur-md p-6 shadow-lg rounded-xl">
          <CardHeader className="p-0 pb-6">
            <CardTitle className="text-white">
              Send us a message
            </CardTitle>
            <CardDescription className="text-slate-400">
              Fill out the form below and we'll get back to you as soon as
              possible.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Input
                    id="firstName"
                    placeholder="First Name"
                    className="border-0 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-600"
                  />
                </div>
                <div className="space-y-2">
                  <Input
                    id="lastName"
                    placeholder="Last Name"
                    className="border-0 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-600"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Input
                  id="email"
                  type="email"
                  placeholder="Email"
                  className="border-0 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-600"
                />
              </div>
              <div className="space-y-2">
                <Input
                  id="company"
                  placeholder="Company"
                  className="border-0 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-600"
                />
              </div>
              <div className="space-y-2">
                <Input
                  id="subject"
                  placeholder="Subject"
                  className="border-0 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-600"
                />
              </div>
              <div className="space-y-2">
                <Textarea
                  id="message"
                  placeholder="Message"
                  rows={4}
                  className="border-0 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-600 resize-none"
                />
              </div>
              <Button
                type="submit"
                className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors cursor-pointer px-6 py-2 shadow-md"
              >
                Send Message
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Info Cards Side Section */}
        <div className="space-y-8">
          {/* Get in touch Card */}
          <Card className="border border-white/10 bg-slate-900/40 backdrop-blur-md p-6 shadow-lg rounded-xl">
            <CardHeader className="p-0 pb-6">
              <CardTitle className="text-white">
                Get in touch
              </CardTitle>
              <CardDescription className="text-slate-400">
                Reach out to us through any of these channels.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 p-0">
              <div className="flex items-center space-x-4">
                <div className="p-2.5 rounded-lg bg-white/5 border border-white/10">
                  <Mail className="h-5 w-5 text-cyan-400" />
                </div>
                <div>
                  <p className="font-semibold text-white">
                    Email
                  </p>
                  <p className="text-sm text-slate-300">
                    support@usland.com
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="p-2.5 rounded-lg bg-white/5 border border-white/10">
                  <Phone className="h-5 w-5 text-cyan-400" />
                </div>
                <div>
                  <p className="font-semibold text-white">
                    Phone
                  </p>
                  <p className="text-sm text-slate-300">
                    1-800-USLAND
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="p-2.5 rounded-lg bg-white/5 border border-white/10">
                  <MapPin className="h-5 w-5 text-cyan-400" />
                </div>
                <div>
                  <p className="font-semibold text-white">
                    Address
                  </p>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    123 Business Ave
                    <br />
                    Dallas, TX 75201
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Business Hours Card */}
          <Card className="border border-white/10 bg-slate-900/40 backdrop-blur-md p-6 shadow-lg rounded-xl">
            <CardHeader className="p-0 pb-4">
              <CardTitle className="text-white">
                Business Hours
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="space-y-3">
                <div className="flex justify-between items-center border-b border-white/5 pb-2">
                  <span className="text-slate-300">
                    Monday - Friday
                  </span>
                  <span className="font-medium text-white">
                    9:00 AM - 6:00 PM CST
                  </span>
                </div>
                <div className="flex justify-between items-center border-b border-white/5 pb-2">
                  <span className="text-slate-300">
                    Saturday
                  </span>
                  <span className="font-medium text-white">
                    10:00 AM - 4:00 PM CST
                  </span>
                </div>
                <div className="flex justify-between items-center pt-1">
                  <span className="text-slate-300">
                    Sunday
                  </span>
                  <span className="font-medium text-red-400">
                    Closed
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}